import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UserPreferenceArrayUpdateCriteria } from '../model/searchCriteria';
import { constants } from '../constants';

@Injectable()
export class UserPreferenceService {
    
    private userPrefURL = environment.endpoints.userpreferences + 'preferences';
    private userPrefUpdateSingle = environment.endpoints.userpreferences + 'updateSinglePreference';
    private userPrefUpdateArray = environment.endpoints.userpreferences + 'updateArrayPreference';
    private userPrefUpdateLastLogin = environment.endpoints.userpreferences + 'updateLastLogin';
    private userPrefUpdateReferences = environment.endpoints.userpreferences + 'updateStringReferences';
    private userPrefGetNotifications = environment.endpoints.userpreferences + 'getNotifications';
    private userPrefUserNotif = environment.endpoints.userpreferences + 'updateUserNotif';

    constructor(private http: HttpClient) { }


    getNotifications(): Promise<[]> {
        return this.http
            .get(this.userPrefGetNotifications)
            .toPromise()
            .then(res => res)
            .catch(this.handleError);
    }
    updateUserPrefNotif(notif: any) {
        let _id = notif[0]._id.$oid
        sessionStorage.removeItem(constants.local.userPreferences)
        return this.http
        .get(this.userPrefUserNotif + "/" + _id)
        .toPromise()
        .then(res => res)
        .catch(this.handleError)
      }
  
    /**
     * Get all the users preferences for a logged in user
     * Tries to get them locally, if not then retrieve from db
     * Preferences loaded at login and app initialisation
     */
    getUserPreferences(): Promise<any> {
        return new Promise((resolve,reject) => {
            if(JSON.parse(sessionStorage.getItem(constants.local.userPreferences))){
                resolve(JSON.parse(sessionStorage.getItem(constants.local.userPreferences)));
            }else{
                return this.http
                    .get(this.userPrefURL)
                    .toPromise()
                    .then(response => sessionStorage.setItem(constants.local.userPreferences, JSON.stringify(response)))
                    .then(() => resolve(JSON.parse(sessionStorage.getItem(constants.local.userPreferences))))
                    .catch(err => reject(err));
            }
        });
    }

    /**
     * Get the last login date for the user from the
     * user preferences
     * This data is cached in sessionStorage at app init
     * so that any subsequent refreshes dont update it
     * It will look for the lastLogin cache before reverting back to 
     * the user preferences object
     */
    getUserLastLoginDate(): Promise<Date> {
        return new Promise((resolve,reject) => {
            if(sessionStorage.getItem("lastLogin") && sessionStorage.getItem("lastLogin") != 'undefined'){
                resolve(new Date(Number(sessionStorage.getItem("lastLogin"))));
            }else{
                this.getUserPreferences()
                    .then(res => {
                        if(res.lastLogin){
                            resolve(new Date(res.lastLogin));
                        }else{
                            resolve(new Date());
                        }
                    })
                    .catch(err => reject(err));
            }
        });
    }

    /**
     * Update the last login date for a user
     */
    userUpdateLastLogin(): Promise<void> {
        return this.http
            .post(this.userPrefUpdateLastLogin, {})
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }

    /**
     * Update a single user preference attribute with a value
     * Once complete update the local user preferences to match
     * @param attribute 
     * @param value 
     */
    userPreferencesUpdateSingle(attribute: string, value: any): Promise<any> {
        return new Promise((resolve,reject) => {
            this.userPrefencesUpdateSinglePrivate(attribute, value)
                .then(res => {
                    this.refreshLocalUserPreferences();
                    resolve(res)
                })
                .catch(err => reject(err));
        });
    }

    /**
     * Update an item within an array attribute of user preferences
     * Once complete update the local user preferences to match
     * @param arrayName 
     * @param item 
     * @param deleted 
     */
    userPreferencesUpdateArray(arrayName: string, item: {}, deleted: boolean): Promise<any> {
        return new Promise((resolve,reject) => {
            this.userPrefencesUpdateArrayPrivate(arrayName, item, deleted)
                .then(res => {
                    this.refreshLocalUserPreferences();
                    resolve(res)
                })
                .catch(err => reject(err));
        });
    }

     /**
     * Update an item within an array attribute of user preferences
     * The array is a child of the topLevelObject to allow better data structure
     * Once complete update the local user preferences to match
     * @param arrayName 
     * @param item 
     * @param deleted 
     */
    userPreferencesUpdateChildArray(topLevelObject: string, arrayName: string, item: {}, deleted: boolean): Promise<any> {
        return new Promise((resolve,reject) => {
            this.userPrefencesUpdateChildArrayPrivate(topLevelObject, arrayName, item, deleted)
                .then(res => {
                    this.refreshLocalUserPreferences();
                    resolve(res)
                })
                .catch(err => reject(err));
        });
    }

    /**
     * Update the users flagged items
     * Each itemtype has its own array attribute in user preferences object
     * constants file contains a list of the item types
     * @param itemType 
     * @param reference 
     * @param deleted 
     */
    userPreferencesUpdateFlags(itemType: string, reference: string, deleted: boolean): Promise<any> {
        return new Promise((resolve,reject) => {
            this.userPrefencesUpdateReferencesPrivate("flags", itemType, reference, deleted)
                .then(res => {
                    this.refreshLocalUserPreferences();
                    resolve(res)
                })
                .catch(err => reject(err));
        });
    }

      /**
     * Update the users watchedProducts items
     * Each itemtype has its own array attribute in user preferences object
     * constants file contains a list of the item types
     * @param itemType 
     * @param reference 
     * @param deleted 
     */
    userPreferencesUpdateWatchedProducts(itemType: string, reference: string, deleted: boolean): Promise<any> {
        return new Promise((resolve,reject) => {
            this.userPrefencesUpdateReferencesPrivate("watched", itemType, reference, deleted)
                .then(res => {
                    this.refreshLocalUserPreferences();
                    resolve(res)
                })
                .catch(err => reject(err));
        });
    }
    
    /**
     * Refresht the locally stored preferences by deleting and recreating
     */
    private refreshLocalUserPreferences() {
        sessionStorage.removeItem(constants.local.userPreferences);
        this.getUserPreferences();
    }

    /**
     * Access the endpoint to update a string array of references for a given attribute type for an item
     * @param attributeType - the type of reference eg) flags
     * @param itemType - the object type eg) signals
     * @param reference - the reference
     * @param deleted - whether to delete
     */
    private userPrefencesUpdateReferencesPrivate(attributeType: string, itemType: string, reference: string, deleted: boolean): Promise<any> {
        return this.http
            .post(this.userPrefUpdateReferences, JSON.stringify({attributeType: attributeType, itemType: itemType, reference: reference, delete: deleted}))
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }

    /**
     * Access the endpoint to update single attribute
     * @param attribute 
     * @param value 
     */
    private userPrefencesUpdateSinglePrivate(attribute: string, value: any): Promise<any> {
        let criteria = {}
        criteria[attribute] = value;
        return this.http
            .post(this.userPrefUpdateSingle, JSON.stringify(criteria))
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }

    /**
     * Access the endpoint to update arrays
     * @param arrayName 
     * @param item 
     * @param deleted 
     */
    private userPrefencesUpdateArrayPrivate(arrayName: string, item: {}, deleted: boolean): Promise<any> {
        let criteria = new UserPreferenceArrayUpdateCriteria();
        criteria.arrayName = arrayName;
        criteria.item = item;
        criteria.delete = deleted;

        return this.http
            .post(this.userPrefUpdateArray, JSON.stringify(criteria))
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }

    /**
     * Access the endpoint to update arrays
     * @param arrayName 
     * @param item 
     * @param deleted 
     */
    private userPrefencesUpdateChildArrayPrivate(topLevelObject: string, arrayName: string, item: {}, deleted: boolean): Promise<any> {
        let criteria = new UserPreferenceArrayUpdateCriteria();
        criteria.topLevelObject = topLevelObject;
        criteria.arrayName = arrayName;
        criteria.item = item;
        criteria.delete = deleted;

        return this.http
            .post(this.userPrefUpdateArray, JSON.stringify(criteria))
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }


    /**
     * Generic error handler for this service
     * @param error 
     */
    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }

}